import { useCallback, useMemo, useState } from 'react';
import { IRedirect } from '@/models/redirect.model';
import { TableRowSelection } from 'antd/es/table/interface';

const useChosenRedirects = () => {
  const [chosenRedirects, setChosenRedirects] = useState<string[]>([]);

  const handleResetChosenRedirects = useCallback(() => {
    setChosenRedirects([]);
  }, []);

  const handleSelectOneRedirect = useCallback((redirect: IRedirect, selected: boolean) => {
    setChosenRedirects((prev) => (selected ? [...prev, redirect.id] : prev.filter((id) => id !== redirect.id)));
  }, []);

  const handleSelectAllRedirectsOnPage = useCallback((changeableRowKeys: string[]) => {
    setChosenRedirects((prev) => Array.from(new Set([...prev, ...changeableRowKeys])));
  }, []);

  const handleResetChosenRedirectsOnPage = useCallback((changeableRowKeys: string[]) => {
    setChosenRedirects((prev) => prev.filter((id) => !changeableRowKeys.includes(id)));
  }, []);

  const handleToggleAllSelectedRedirectsOnPage = useCallback(
    (selected: boolean, changeableRowKeys: string[]) => {
      if (selected) {
        return handleSelectAllRedirectsOnPage(changeableRowKeys);
      }
      handleResetChosenRedirectsOnPage(changeableRowKeys);
    },
    [handleSelectAllRedirectsOnPage, handleResetChosenRedirectsOnPage],
  );

  const rowSelection: TableRowSelection<IRedirect> = useMemo(
    () => ({
      columnWidth: 64,
      selectedRowKeys: chosenRedirects,
      selections: [
        {
          key: 'page-data',
          text: 'Select all on this page',
          onSelect: async (changeableRowKeys) => {
            handleSelectAllRedirectsOnPage(changeableRowKeys as string[]);
          },
        },

        {
          key: 'clear-page-data',
          text: 'Clear all on this page',
          onSelect: async (changeableRowKeys) => {
            handleResetChosenRedirectsOnPage(changeableRowKeys as string[]);
          },
        },

        {
          key: 'clear-all-data',
          text: 'Clear all',
          onSelect: handleResetChosenRedirects,
        },
      ],

      preserveSelectedRowKeys: true,
      onSelect: handleSelectOneRedirect,
      onSelectAll: (selected, _selectedRows, changeRows) => {
        const changedKeys = changeRows.map<string>((redirect) => redirect.id);
        handleToggleAllSelectedRedirectsOnPage(selected, changedKeys);
      },
    }),
    [
      chosenRedirects,
      handleResetChosenRedirects,
      handleResetChosenRedirectsOnPage,
      handleSelectAllRedirectsOnPage,
      handleSelectOneRedirect,
      handleToggleAllSelectedRedirectsOnPage,
    ],
  );

  return useMemo(
    () => ({
      chosenRedirects,
      rowSelection,
      handleResetChosenRedirects,
      handleSelectOneRedirect,
      handleSelectAllRedirectsOnPage,
      handleToggleAllSelectedRedirectsOnPage,
      handleResetChosenRedirectsOnPage,
    }),
    [
      chosenRedirects,
      rowSelection,
      handleResetChosenRedirects,
      handleSelectOneRedirect,
      handleSelectAllRedirectsOnPage,
      handleToggleAllSelectedRedirectsOnPage,
      handleResetChosenRedirectsOnPage,
    ],
  );
};

export default useChosenRedirects;
